<template>
    <div
        :id="id"
        v-click-away="hide"
        :class="['kc-suggestion-input', {'active': shown, disabled, readonly, 'is-invalid': state === false}]"
        @mouseenter="mouseover = true"
        @mouseover="mouseover = true"
        @mouseleave="mouseover = false"
        @mouseout="mouseover = false"
    >
        <button
            :id="`${id}MenuButton`"
            class="btn btn-dropdown"
            type="button"
            aria-haspopup="true"
            :aria-expanded="shown ? 'true' : 'false'"
            @focus="show($event)"
        >
            <input
                v-show="shown"
                ref="input"
                v-model="input"
                class="filter-control"
                type="text"
                :placeholder="selectionText"
                @focus="isFocused = true"
                @blur="hide($event, mouseover)"
                @keydown.esc.tab="hide($event, false)"
                @input="handleInput($event.target.value)"
                @keydown.up="moveHighlight(-1)"
                @keydown.down="moveHighlight(1)"
                @keydown.enter="enterSelect"
            >

            <span
                v-show="!shown"
                class="selection mr-auto"
            >
                {{ selectionText }}
            </span>
        </button>
        <div
            v-show="isFocused || loading"
            :class="['dropdown-menu', 'select', {'show': shown}]"
            :aria-labelledby="`${id}MenuButton`"
        >
            <div
                v-if="validSuggestions.length || loading"
                class="suggestion-header"
                data-cy="suggestionHeader"
            >
                <slot name="suggestion-header">
                    {{ suggestionHeader }}
                </slot>
            </div>
            <div
                v-if="loading"
                class="ellipsis-loader px-2 pt-2 pb-3"
            >
                <FontAwesomeIcon
                    icon="circle"
                    class="fas fa-circle fa-xs mx-1 loading-dot-1"
                />
                <FontAwesomeIcon
                    icon="circle"
                    class="fas fa-circle fa-xs mr-1 loading-dot-2"
                />
                <FontAwesomeIcon
                    icon="circle"
                    class="fas fa-circle fa-xs loading-dot-3"
                />
            </div>
            <div v-else>
                <DropdownItem
                    v-for="(suggestion, index) in validSuggestions"
                    :key="index"
                    :text="suggestion.text"
                    :value="suggestion.value"
                    :classes="[suggestion.classes]"
                    :highlighted="suggestion === highlightedOption"
                    @click="select($event)"
                >
                    <slot
                        name="suggestion"
                        :suggestion="suggestion"
                    >
                        {{ suggestion.text }}
                    </slot>
                </DropdownItem>
            </div>
        </div>
    </div>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {faCircle} from '@fortawesome/pro-solid-svg-icons';
    import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';

    import {ClickAway} from '../directives/click-away';
    import DropdownItem from './DropdownItem.vue';

    library.add(
        faCircle
    );

    export default {
        name: 'KCSuggestionInput',
        components: {
            DropdownItem,
            FontAwesomeIcon
        },
        directives: {
            'click-away': ClickAway,
        },
        model: {
            prop: 'value',
            event: `input`
        },
        props: {
            /**
             * The ID of the suggestion element
             */
            id: {
                type: String,
                required: true,
            },
            /**
             * An array of suggestion objects to display
             */
            suggestions: {
                type: Array,
                required: false,
                default() {
                    return [];
                },
            },
            /**
             * Sets the initially selected option. Must match one of the option's `value` keys.
             */
            value: {
                type: [Array, Number, Object, String],
                required: false,
                default() {
                    return [];
                },
            },
            /**
             * Whether the suggestions are disabled, when `true` disables the ability to open or change the value selected and adds the `disabled` class to the suggestion element
             */
            disabled: {
                type: Boolean,
                required: false,
                default: false,
            },
            /**
             * Whether the suggestions are readonly, when `true` disables the ability to open or change the value selected. Updates styling.
             */
            readonly: {
                type: Boolean,
                required: false,
                default: false,
            },
            /**
             * The placeholder to be displayed when no selection is made.
             */
            placeholder: {
                type: String,
                required: false,
                default: 'begin typing...',
            },
            /**
             * Whether the input is in a valid or invalid state
             */
            state: {
                type: Boolean,
                required: false,
                default: null,
            },
            /**
             * Whether the input is loading suggestions
             */
            loading: {
                type: Boolean,
                required: true,
                default: true
            }
        },
        data() {
            return {
                // whether the cursor is over the suggestions or any of its children elements.
                mouseover: false,
                // whether the suggestions menu is shown
                shown: false,
                // Stores highlighted option for accessibility keys
                highlightedOption: null,
                // text value of the input
                input: "",
                // If input is focused
                isFocused: false,
            };
        },
        computed: {
            /**
             * Gets the selection text or placeholder for the default text input placeholder.
             *
             * @returns String of the currently selected values or the placeholder prop
             */
            selectionText() {
                if (Array.isArray(this.value)) {
                    if (this.value.length) {
                        const matchingObject = this.suggestions.find(obj => obj.value === this.value[0].value);
                        if (matchingObject) {
                            return matchingObject.text;
                        }
                    }
                } else {
                    const matchingObject = this.suggestions.find(obj => obj.value === (this.value?.value || this.value.toString()));
                    if (this.value && matchingObject) {
                        return matchingObject.text;
                    }
                }

                return this.input ? this.input : this.placeholder;
            },
            suggestionHeader() {
                if (!this.loading) {
                    return 'Select one';
                } else {
                    return 'Loading suggestions';
                }
            },
            validSuggestions() {
                return this.suggestions.filter(suggestion => suggestion && suggestion.value);
            }
        },
        watch: {
            shown(isShown) {
                if (isShown) {
                    // Emitted when the suggestions are shown
                    this.$emit(`shown`);
                } else {
                    // Emitted when the suggestions are hidden
                    this.$emit(`hidden`);
                }
            },
        },
        methods: {
            /**
             * Gets the index of the option provided based on the `value` keys.
             *
             * @param option: the object of the option from `suggestions`
             * @returns {number} or -1 id not found in suggestion values
             */
            getSelectedIndex(option) {
                if (this.selectionText === option.text) {
                    return this.suggestions.map((suggestion) => {
                        return suggestion.text;
                    }).indexOf(this.selectionText);
                }

                return -1;
            },
            /**
             * Hides the suggestions if `mouseover` is false.
             *
             * @param event
             * @param mouseover: boolean if the cursor is over the suggestions or children elements
             */
            hide(event, mouseover) {
                if (!mouseover) {
                    this.shown = false;
                    this.isFocused = false;
                }
            },
            /**
             * Toggles `shown` if the suggestions are not disabled and the event is not a phantom event and focuses on the input if the suggestions are shown.
             */
            show(event) {
                if (!this.disabled && !this.readonly) {
                    // event.screenX & event.screenY are a fix for space key bug triggering a click
                    if (event.screenX !== 0 && event.screenY !== 0 && event.target.type !== 'text') {
                        this.shown = !this.shown;
                        if (this.shown) {
                            this.$nextTick(() => {
                                this.$refs.input.focus();
                            });
                        }
                    }
                }
            },
            /**
             * Sets the `value` to the provided selected. Hides the suggestions if necessary.
             *
             * @param selected: the object of the option that was selected
             */
            select(selected) {
                let value = [selected];
                this.mouseover = false;
                this.input = value?.[0]?.text || "";
                // Emitted when a suggestion item is selected
                this.$emit(`input`, value);
                this.hide({}, this.mouseover);
            },
            /**
             * Updates the `highlightedOption` by the delta param.
             *
             * @param delta: number of places to move the index generally -1 or 1
             */
            moveHighlight(delta) {
                const currentIndex = this.suggestions.findIndex(option => option === this.highlightedOption);
                let newIndex = (currentIndex + delta + this.suggestions.length) % this.suggestions.length;
                this.highlightedOption = this.suggestions[newIndex];
            },
            /**
             * On enter keydown selects the option of the highlightedOptionIndex
             */
            enterSelect() {
                this.select(this.highlightedOption);
            },
            /**
             * Set input to the value given
             *
             * @param value - value to set input to
             */
            handleInput(value) {
                this.input = value;
                this.$emit('input', value);
            }
        }
    };
</script>

<style lang="sass">
.ellipsis-loader
  display: flex

  .fas
    color: var(--kc-blue-400)

    &.loading-dot-1
      animation: loading-1 1.5s infinite

    &.loading-dot-2
      animation: loading-2 1.5s infinite

    &.loading-dot-3
      animation: loading-3 1.5s infinite

@keyframes loading-1
  0%, 66%, 100%
    color: var(--kc-blue-400)
  33%
    color: var(--kc-blue-800)

@keyframes loading-2
  0%, 33%, 100%
    color: var(--kc-blue-400)
  66%
    color: var(--kc-blue-800)

@keyframes loading-3
  0%, 33%, 66%
    color: var(--kc-blue-400)
  100%
    color: var(--kc-blue-800)
</style>
